/**
 * SG-01 Segment bimodality (INV-CH-SG-01, ENGINE-SPEC §7.5; change C-D3-SG01, the D3 fix).
 *
 * Units come from the text (§2.9): chapters if there are ≥ 8, else scenes if ≥ 8, else prose
 * paragraphs of ≥ 12 words. Each unit's lean is the word-weighted mean of the seed `segLean` of
 * its paragraphs (paragraphs with fewer than 12 words have no lean). At least 8 units with a
 * lean are required, otherwise the channel ABSTAINS (the 4-paragraph D3 fixture cannot set
 * `assisted`). The seed's statistics run over the units: hiF (share with lean > 0.30), loF
 * (share < −0.15), bimodal = hiF ≥ 0.2 && loF ≥ 0.15, spread = max − min.
 *
 * Permutation test: T = the word-weighted variance of the unit leans; the null permutes the
 * paragraphs across the units keeping each unit's paragraph count (B = 199,
 * p = (1 + #{T* ≥ T})/200). The seed mapping applies only when bimodal and p ≤ 0.01; otherwise
 * the score is the seed's non-bimodal 12 (32 when p ≤ 0.01 and hiF ≥ 0.6) with κ = 0.2.
 *
 * Ambiguity (registered): when the units are single paragraphs the permutation null is the
 * identity (every unit holds one paragraph), so p = 1 and the channel can only report the
 * non-bimodal score; it is significant only when chapters or scenes exist. This is the most
 * conservative reading of §7.5 step 4.
 */
import { makeRng } from '../util/rng.ts';
import { clamp, ramp } from '../util/stats.ts';
import { abstainResult, derivePrior, languageGate, scoredResult } from './framework.ts';
import { channelSeed, fmt, makeEvidence, proseParagraphs } from './helpers.ts';
import { segLean } from './seglean.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Ramp,
	RunContext,
	UnitEstimate,
} from './types.ts';

const RAMP: Ramp = [
	[0.08, 55],
	[0.15, 68],
	[0.25, 80],
	[0.4, 90],
];
const HI = 0.3;
const LO = -0.15;
const MIN_UNITS = 8;
const B = 199;
const ALPHA = 0.01;

export const sg01Def: ChannelDef = {
	id: 'SG-01',
	name: 'Segment bimodality',
	family: 'SEGMENT',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: true,
	estimator: 'structural',
	features: [{ id: 'bimodal', label: 'min(machine-lean share, human-lean share) × lean spread' }],
	minExposure: MIN_UNITS,
	exposureUnit: 'units',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'min(hi, lo) share × lean spread',
	prior: derivePrior(RAMP, 1, 'units', 1.2),
	weight: 1.2,
	nd: 0.15,
	changes: ['C-D3-SG01'],
};

interface Unit {
	/** Index into doc.chapters / doc.scenes, or the paragraph index for paragraph units. */
	index: number;
	paraStart: number;
	paraEnd: number;
	/** Indices into the flat arrays of valid paragraphs. */
	first: number;
	count: number;
}

function run(ctx: RunContext): ChannelResult {
	const def = sg01Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const P = doc.paragraphs.count;

	// leans of the prose paragraphs
	const lean = new Float64Array(P).fill(Number.NaN);
	const weight = new Float64Array(P);
	for (const p of proseParagraphs(doc)) {
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const l = segLean(doc.text.slice(b.charStart, b.charEnd));
		if (l !== null) {
			lean[p] = l;
			weight[p] = doc.paragraphs.ownWords[p] as number;
		}
	}

	// units (§2.9): chapters if ≥ 8, else scenes if ≥ 8, else paragraphs of ≥ 12 words
	let kind: 'chapter' | 'scene' | 'paragraph';
	if (doc.chapters.length >= MIN_UNITS) kind = 'chapter';
	else if (doc.scenes.length >= MIN_UNITS) kind = 'scene';
	else kind = 'paragraph';
	const pl: number[] = []; // lean of each valid paragraph, grouped by unit
	const pw: number[] = [];
	const pIdx: number[] = [];
	const units: Unit[] = [];
	if (kind === 'paragraph') {
		for (const p of proseParagraphs(doc)) {
			if (Number.isNaN(lean[p] as number) || (doc.paragraphs.ownWords[p] as number) < 12) continue;
			units.push({ index: p, paraStart: p, paraEnd: p + 1, first: pl.length, count: 1 });
			pl.push(lean[p] as number);
			pw.push(weight[p] as number);
			pIdx.push(p);
		}
	} else {
		const src = kind === 'chapter' ? doc.chapters : doc.scenes;
		src.forEach((u, index) => {
			const first = pl.length;
			for (let p = u.paraStart; p < u.paraEnd; p++) {
				if (Number.isNaN(lean[p] as number)) continue;
				pl.push(lean[p] as number);
				pw.push(weight[p] as number);
				pIdx.push(p);
			}
			const count = pl.length - first;
			if (count > 0)
				units.push({ index, paraStart: u.paraStart, paraEnd: u.paraEnd, first, count });
		});
	}
	const J = units.length;
	if (J < MIN_UNITS) return abstainResult(def, `insufficient units: ${J} < ${MIN_UNITS}`, notes);

	// unit leans and the statistics
	const uLean = new Float64Array(J);
	const uW = new Float64Array(J);
	for (let u = 0; u < J; u++) {
		const { first, count } = units[u] as Unit;
		let sw = 0;
		let sl = 0;
		for (let i = first; i < first + count; i++) {
			sw += pw[i] as number;
			sl += (pw[i] as number) * (pl[i] as number);
		}
		uW[u] = sw;
		uLean[u] = sw > 0 ? sl / sw : 0;
	}
	let hi = 0;
	let lo = 0;
	let mn = Number.POSITIVE_INFINITY;
	let mx = Number.NEGATIVE_INFINITY;
	for (const l of uLean) {
		if (l > HI) hi++;
		if (l < LO) lo++;
		mn = Math.min(mn, l);
		mx = Math.max(mx, l);
	}
	const hiF = hi / J;
	const loF = lo / J;
	const spread = mx - mn;
	const bimodal = hiF >= 0.2 && loF >= 0.15;
	const weightedVar = (leans: ArrayLike<number>, ws: ArrayLike<number>): number => {
		let sw = 0;
		let sl = 0;
		for (let i = 0; i < leans.length; i++) {
			sw += ws[i] as number;
			sl += (ws[i] as number) * (leans[i] as number);
		}
		const m = sw > 0 ? sl / sw : 0;
		let v = 0;
		for (let i = 0; i < leans.length; i++) v += (ws[i] as number) * ((leans[i] as number) - m) ** 2;
		return sw > 0 ? v / sw : 0;
	};
	const T = weightedVar(uLean, uW);

	// permutation test: paragraphs permuted across units, unit paragraph counts kept
	let pValue = 1;
	let z = 0;
	const degenerate = units.every((u) => u.count === 1);
	if (degenerate) {
		notes.push('paragraph units: the permutation null is degenerate (p = 1)');
	} else {
		const rng = makeRng(channelSeed(doc, 0x53470001));
		const order = new Uint32Array(pl.length);
		for (let i = 0; i < order.length; i++) order[i] = i;
		const sl = new Float64Array(J);
		const sw = new Float64Array(J);
		let exceed = 0;
		let sum = 0;
		let sum2 = 0;
		for (let b = 0; b < B; b++) {
			for (let i = order.length - 1; i > 0; i--) {
				const j = Math.floor(rng() * (i + 1));
				const t = order[i] as number;
				order[i] = order[j] as number;
				order[j] = t;
			}
			let cursor = 0;
			for (let u = 0; u < J; u++) {
				const cnt = (units[u] as Unit).count;
				let w = 0;
				let l = 0;
				for (let r = 0; r < cnt; r++) {
					const q = order[cursor++] as number;
					w += pw[q] as number;
					l += (pw[q] as number) * (pl[q] as number);
				}
				sw[u] = w;
				sl[u] = w > 0 ? l / w : 0;
			}
			const Tb = weightedVar(sl, sw);
			if (Tb >= T - 1e-12) exceed++;
			sum += Tb;
			sum2 += Tb * Tb;
		}
		pValue = (1 + exceed) / (B + 1);
		const mean = sum / B;
		const sd = Math.sqrt(Math.max(0, sum2 / B - mean * mean));
		z = sd > 0 ? (T - mean) / sd : 0;
	}

	const significant = pValue <= ALPHA;
	const estimate = Math.min(hiF, loF) * spread;
	let score: number;
	let kappa: number;
	if (bimodal && significant) {
		score = ramp(estimate, RAMP);
		kappa = clamp(J / 8, 0.35, 0.8);
	} else {
		score = significant && hiF >= 0.6 ? 32 : 12;
		kappa = 0.2;
	}

	const ev = makeEvidence();
	if (bimodal && significant) {
		for (let i = 0; i < pIdx.length; i++) {
			const p = pIdx[i] as number;
			const b = doc.blocks[doc.paragraphs.block[p] as number];
			if (!b) continue;
			const l = pl[i] as number;
			if (l > HI) ev.add(b.charStart, b.charEnd, 0, l);
			else if (l < LO) ev.add(b.charStart, b.charEnd, 1, -l);
		}
	}

	const unitEst: UnitEstimate[] = [];
	if (kind !== 'paragraph') {
		for (let u = 0; u < J; u++) {
			const un = units[u] as Unit;
			unitEst.push({
				kind,
				index: un.index,
				paraStart: un.paraStart,
				paraEnd: un.paraEnd,
				K: un.count,
				E: uW[u] as number,
				estimate: uLean[u] as number,
				ci90: [uLean[u] as number, uLean[u] as number],
				score,
				elevated: false,
				pRef: 1,
			});
		}
	}

	const res = scoredResult(def, {
		K: J,
		E: J,
		raw: estimate,
		estimate,
		deviation: z,
		phi: 1,
		kappa,
		score,
		scoreCi90: [score, score],
		evidence: ev.build(),
		notes,
		features: {
			hiShare: hiF,
			loShare: loF,
			spread,
			variance: T,
			pPermutation: pValue,
			units: J,
			bimodal: bimodal ? 1 : 0,
			significant: significant ? 1 : 0,
		},
		summary: [
			`${hi} machine-lean · ${lo} human-lean · ${J} segments (lean thresholds +0.30 / −0.15)`,
			`spread ${fmt(spread, 2)} · permutation p = ${fmt(pValue, 3)}`,
		],
	});
	res.units = unitEst;
	return res;
}

export const sg01: ChannelImpl = { def: sg01Def, phase: 'derived', run };
